import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { HeroSection } from '../../components/HeroSection';
import { motion } from 'framer-motion';
import { AlertTriangle, ArrowRight, Check, X, Minus, BookOpen, Lightbulb } from 'lucide-react';
export function FatFrameworks() {
  const [activeSize, setActiveSize] = useState<number | null>(null);
  const spectrum = [{
    label: 'Smallfat',
    sizes: 'US 14–18',
    color: 'bg-brand-magenta/20',
    borderColor: 'border-brand-magenta',
    textColor: 'text-brand-magenta',
    description: 'People at the lower end of the fat spectrum. Often able to shop in straight-size stores, fit in most public seating, and navigate medical care with less friction.',
    access: {
      clothing: 'high',
      seating: 'high',
      medical: 'moderate',
      employment: 'moderate',
      representation: 'moderate',
      mobility: 'high'
    },
    notes: 'Smallfats experience fatphobia but retain significant access and privilege compared to larger fat people. Important to acknowledge this privilege within fat spaces.'
  }, {
    label: 'Midfat',
    sizes: 'US 18–24',
    color: 'bg-brand-magenta/35',
    borderColor: 'border-brand-magenta',
    textColor: 'text-brand-magenta',
    description: 'People in the middle of the fat spectrum. May struggle to find clothing in mainstream stores, experience some seating limitations, and face more medical discrimination.',
    access: {
      clothing: 'moderate',
      seating: 'moderate',
      medical: 'low',
      employment: 'low',
      representation: 'low',
      mobility: 'moderate'
    },
    notes: 'Midfats often straddle the line between "acceptable" and "unacceptable" fatness. They may be included in corporate body positivity but excluded from true fat liberation resources.'
  }, {
    label: 'Superfat',
    sizes: 'US 24–32',
    color: 'bg-brand-magenta/55',
    borderColor: 'border-brand-magenta',
    textColor: 'text-brand-magenta',
    description: 'People at the higher end of the fat spectrum. Face significant barriers in clothing, public spaces, healthcare, and employment. Often invisible in media representation.',
    access: {
      clothing: 'low',
      seating: 'low',
      medical: 'very-low',
      employment: 'very-low',
      representation: 'very-low',
      mobility: 'low'
    },
    notes: 'Superfats face compounded discrimination. Medical providers may refuse care, employers may not hire, and public spaces are often physically inaccessible.'
  }, {
    label: 'Infinifat',
    sizes: 'US 32+',
    color: 'bg-brand-magenta/75',
    borderColor: 'border-brand-magenta',
    textColor: 'text-white',
    description: 'People at the largest end of the fat spectrum. Face the most extreme barriers across all areas of life. Almost completely erased from media, fashion, and public discourse.',
    access: {
      clothing: 'very-low',
      seating: 'very-low',
      medical: 'very-low',
      employment: 'very-low',
      representation: 'very-low',
      mobility: 'very-low'
    },
    notes: "Infinifats are the most marginalized within the fat community. Fat liberation must center their experiences and access needs, or it's not liberation—it's just slightly expanded acceptability."
  }];
  const accessAreas = [{
    key: 'clothing',
    label: 'Clothing Access',
    description: 'Ability to find clothing in stores and online'
  }, {
    key: 'seating',
    label: 'Public Seating',
    description: 'Fitting in chairs, booths, transit, theaters'
  }, {
    key: 'medical',
    label: 'Medical Care',
    description: 'Receiving dignified, weight-neutral healthcare'
  }, {
    key: 'employment',
    label: 'Employment',
    description: 'Hiring, workplace treatment, advancement'
  }, {
    key: 'representation',
    label: 'Media Representation',
    description: 'Seeing yourself in media, fashion, culture'
  }, {
    key: 'mobility',
    label: 'Physical Mobility',
    description: 'Moving through public spaces without barriers'
  }];
  const getAccessIcon = (level: string) => {
    switch (level) {
      case 'high':
        return <Check className="h-5 w-5 text-brand-lime" data-id="element-1217" />;
      case 'moderate':
        return <Minus className="h-5 w-5 text-brand-orange" data-id="element-1218" />;
      case 'low':
        return <X className="h-5 w-5 text-brand-magenta" data-id="element-1219" />;
      case 'very-low':
        return <X className="h-5 w-5 text-red-600" data-id="element-1220" />;
      default:
        return null;
    }
  };
  const getAccessLabel = (level: string) => {
    switch (level) {
      case 'high':
        return 'Generally accessible';
      case 'moderate':
        return 'Sometimes accessible';
      case 'low':
        return 'Rarely accessible';
      case 'very-low':
        return 'Almost never accessible';
      default:
        return '';
    }
  };
  return <div className="min-h-screen bg-brand-cream" data-id="element-1221">
      <HeroSection title="Fat Frameworks" subtitle="Understanding the fat spectrum, levels of access, and why size matters within fat liberation." tag="Fat Liberation & Justice" bgColor="magenta" data-id="element-1222" />

      {/* Introduction */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 max-w-4xl mx-auto" data-id="element-1223">
        <div className="prose prose-lg" data-id="element-1224">
          <p className="text-xl text-gray-700 leading-relaxed" data-id="element-1225">
            Not all fat people experience fatphobia the same way. The{' '}
            <strong className="text-brand-magenta" data-id="element-1226">fat spectrum</strong> is a
            framework for understanding how size affects access, privilege, and
            oppression within the fat community. The larger you are, the more
            barriers you face.
          </p>
          <p className="text-lg text-gray-600 mt-4" data-id="element-1227">
            These categories were developed by fat activists to name the
            differences in lived experience that size creates—and to ensure fat
            liberation centers those who are most marginalized.
          </p>
        </div>
      </section>

      {/* The Spectrum - Visual */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto" data-id="element-1228">
        <h2 className="text-3xl font-black uppercase mb-4 text-brand-black" data-id="element-1229">
          The Fat Spectrum
        </h2>
        <p className="text-gray-600 mb-12" data-id="element-1230">
          Click any category to learn more about access and barriers at that
          size.
        </p>

        {/* Spectrum Bar */}
        <div className="mb-12" data-id="element-1231">
          <div className="flex rounded-sm overflow-hidden border-2 border-brand-black" data-id="element-1232">
            {spectrum.map((size, index) => <button key={index} onClick={() => setActiveSize(activeSize === index ? null : index)} className={`flex-1 py-6 px-4 ${size.color} transition-all relative ${activeSize === index ? 'ring-4 ring-brand-magenta ring-inset' : ''}`} data-id="element-1233">
                <div className={`text-center ${index >= 2 ? 'text-white' : 'text-brand-black'}`} data-id="element-1234">
                  <p className="text-lg md:text-xl font-black uppercase" data-id="element-1235">
                    {size.label}
                  </p>
                  <p className="text-xs md:text-sm opacity-75 mt-1" data-id="element-1236">
                    {size.sizes}
                  </p>
                </div>
              </button>)}
          </div>

          {/* Arrow indicating increasing oppression */}
          <div className="flex items-center justify-between mt-4 px-2" data-id="element-1237">
            <span className="text-xs font-bold uppercase text-gray-500" data-id="element-1238">
              More access
            </span>
            <div className="flex-1 mx-4 h-px bg-gradient-to-r from-brand-lime via-brand-orange to-brand-magenta" data-id="element-1239"></div>
            <span className="text-xs font-bold uppercase text-gray-500" data-id="element-1240">
              More barriers
            </span>
          </div>
        </div>

        {/* Expanded Detail */}
        {activeSize !== null && <motion.div key={activeSize} initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} className="bg-white border-2 border-brand-black p-8 mb-12" data-id="element-1241">
            <div className="flex flex-col lg:flex-row gap-8" data-id="element-1242">
              <div className="lg:w-1/3" data-id="element-1243">
                <div className={`inline-block px-4 py-2 ${spectrum[activeSize].color} border-2 ${spectrum[activeSize].borderColor} mb-4`} data-id="element-1244">
                  <span className={`text-2xl font-black uppercase ${activeSize >= 2 ? 'text-white' : 'text-brand-black'}`} data-id="element-1245">
                    {spectrum[activeSize].label}
                  </span>
                </div>
                <p className="text-sm text-gray-500 mb-4" data-id="element-1246">
                  {spectrum[activeSize].sizes}
                </p>
                <p className="text-gray-700 leading-relaxed" data-id="element-1247">
                  {spectrum[activeSize].description}
                </p>
              </div>

              <div className="lg:w-2/3" data-id="element-1248">
                <h4 className="text-sm font-bold uppercase text-gray-500 mb-4" data-id="element-1249">
                  Access Levels:
                </h4>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-id="element-1250">
                  {accessAreas.map(area => {
                const level = spectrum[activeSize].access[area.key as keyof (typeof spectrum)[0]['access']];
                return <div key={area.key} className="flex items-start gap-3 p-3 bg-gray-50" data-id="element-1251">
                        {getAccessIcon(level)}
                        <div data-id="element-1252">
                          <p className="text-sm font-bold" data-id="element-1253">{area.label}</p>
                          <p className="text-xs text-gray-500" data-id="element-1254">
                            {getAccessLabel(level)}
                          </p>
                        </div>
                      </div>;
              })}
                </div>

                <div className="mt-6 p-4 bg-brand-magenta/5 border-l-4 border-brand-magenta" data-id="element-1255">
                  <p className="text-sm text-gray-700 italic" data-id="element-1256">
                    {spectrum[activeSize].notes}
                  </p>
                </div>
              </div>
            </div>
          </motion.div>}
      </section>

      {/* Access Comparison Table */}
      <section className="py-16 bg-white" data-id="element-1257">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1258">
          <h2 className="text-3xl font-black uppercase mb-8 text-brand-black" data-id="element-1259">
            Access at a Glance
          </h2>

          <div className="overflow-x-auto" data-id="element-1260">
            <table className="w-full border-2 border-brand-black" data-id="element-1261">
              <thead data-id="element-1262">
                <tr className="bg-brand-magenta text-white" data-id="element-1263">
                  <th className="p-4 text-left font-black uppercase text-sm border-r-2 border-brand-black" data-id="element-1264">
                    Access Area
                  </th>
                  {spectrum.map((size, i) => <th key={i} className="p-4 text-center font-black uppercase text-sm border-r-2 border-brand-black last:border-r-0" data-id="element-1265">
                      {size.label}
                    </th>)}
                </tr>
              </thead>
              <tbody data-id="element-1266">
                {accessAreas.map((area, i) => <tr key={area.key} className={`border-t-2 border-brand-black ${i % 2 === 1 ? 'bg-gray-50' : ''}`} data-id="element-1267">
                    <td className="p-4 font-bold text-sm border-r-2 border-brand-black" data-id="element-1268">
                      {area.label}
                      <p className="font-normal text-xs text-gray-500 mt-1" data-id="element-1269">
                        {area.description}
                      </p>
                    </td>
                    {spectrum.map((size, j) => {
                  const level = size.access[area.key as keyof typeof size.access];
                  return <td key={j} className="p-4 text-center border-r-2 border-brand-black last:border-r-0" data-id="element-1270">
                          <div className="flex justify-center" data-id="element-1271">
                            {getAccessIcon(level)}
                          </div>
                        </td>;
                })}
                  </tr>)}
              </tbody>
            </table>
          </div>

          <div className="flex flex-wrap gap-6 mt-6 text-xs" data-id="element-1272">
            <div className="flex items-center gap-2" data-id="element-1273">
              <Check className="h-4 w-4 text-brand-lime" data-id="element-1274" />
              <span data-id="element-1275">Generally accessible</span>
            </div>
            <div className="flex items-center gap-2" data-id="element-1276">
              <Minus className="h-4 w-4 text-brand-orange" data-id="element-1277" />
              <span data-id="element-1278">Sometimes accessible</span>
            </div>
            <div className="flex items-center gap-2" data-id="element-1279">
              <X className="h-4 w-4 text-brand-magenta" data-id="element-1280" />
              <span data-id="element-1281">Rarely accessible</span>
            </div>
            <div className="flex items-center gap-2" data-id="element-1282">
              <X className="h-4 w-4 text-red-600" data-id="element-1283" />
              <span data-id="element-1284">Almost never accessible</span>
            </div>
          </div>
        </div>
      </section>

      {/* Why This Matters */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 max-w-6xl mx-auto" data-id="element-1285">
        <h2 className="text-3xl font-black uppercase mb-12 text-brand-black" data-id="element-1286">
          Why This Framework Matters
        </h2>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-8" data-id="element-1287">
          <div className="bg-white border-2 border-brand-black p-8" data-id="element-1288">
            <h3 className="text-xl font-black uppercase mb-4 text-brand-magenta" data-id="element-1289">
              Within Fat Spaces
            </h3>
            <p className="text-gray-700 mb-4 leading-relaxed" data-id="element-1290">
              Without naming size differences, fat spaces default to centering
              smallfat experiences. Smallfats may dominate conversations,
              leadership, and representation while superfats and infinifats
              remain invisible.
            </p>
            <ul className="space-y-2 text-sm text-gray-700" data-id="element-1291">
              <li className="flex items-start" data-id="element-1292">
                <span className="text-brand-magenta mr-2" data-id="element-1293">→</span>Conference
                speakers are disproportionately smallfat
              </li>
              <li className="flex items-start" data-id="element-1294">
                <span className="text-brand-magenta mr-2" data-id="element-1295">→</span>"Fat fashion"
                rarely goes above size 28
              </li>
              <li className="flex items-start" data-id="element-1296">
                <span className="text-brand-magenta mr-2" data-id="element-1297">→</span>Body positive
                campaigns feature mostly smallfats
              </li>
              <li className="flex items-start" data-id="element-1298">
                <span className="text-brand-magenta mr-2" data-id="element-1299">→</span>Access needs
                of larger fat people are afterthoughts
              </li>
            </ul>
          </div>

          <div className="bg-white border-2 border-brand-black p-8" data-id="element-1300">
            <h3 className="text-xl font-black uppercase mb-4 text-brand-magenta" data-id="element-1301">
              Within Broader Society
            </h3>
            <p className="text-gray-700 mb-4 leading-relaxed" data-id="element-1302">
              Understanding the spectrum helps name how anti-fatness escalates
              with size. It's not a binary of "fat or not fat"—it's a gradient
              of increasing exclusion, violence, and erasure.
            </p>
            <ul className="space-y-2 text-sm text-gray-700" data-id="element-1303">
              <li className="flex items-start" data-id="element-1304">
                <span className="text-brand-magenta mr-2" data-id="element-1305">→</span>Medical denial
                increases with size
              </li>
              <li className="flex items-start" data-id="element-1306">
                <span className="text-brand-magenta mr-2" data-id="element-1307">→</span>Employment
                discrimination worsens with size
              </li>
              <li className="flex items-start" data-id="element-1308">
                <span className="text-brand-magenta mr-2" data-id="element-1309">→</span>Physical
                spaces become inaccessible at larger sizes
              </li>
              <li className="flex items-start" data-id="element-1310">
                <span className="text-brand-magenta mr-2" data-id="element-1311">→</span>Social stigma
                and dehumanization intensify
              </li>
            </ul>
          </div>
        </div>
      </section>

      {/* Intersections */}
      <section className="py-20 bg-brand-navy text-white" data-id="element-1312">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1313">
          <h2 className="text-3xl font-black uppercase mb-8" data-id="element-1314">
            Size + Other Identities
          </h2>
          <p className="text-lg text-gray-200 mb-12" data-id="element-1315">
            The fat spectrum doesn't exist in isolation. Size intersects with
            race, disability, gender, class, and other identities to create
            unique experiences of oppression.
          </p>

          <div className="space-y-6" data-id="element-1316">
            <div className="bg-white/10 p-6 backdrop-blur-sm border border-white/20" data-id="element-1317">
              <h3 className="font-bold text-lg text-brand-magenta mb-2" data-id="element-1318">
                Size + Race
              </h3>
              <p className="text-gray-200 text-sm" data-id="element-1319">
                Fat Black women face both anti-Blackness and anti-fatness. At
                larger sizes, this compounds: more medical violence, more
                employment discrimination, more dehumanization. The "mammy"
                stereotype specifically targets fat Black women.
              </p>
            </div>

            <div className="bg-white/10 p-6 backdrop-blur-sm border border-white/20" data-id="element-1320">
              <h3 className="font-bold text-lg text-brand-magenta mb-2" data-id="element-1321">
                Size + Disability
              </h3>
              <p className="text-gray-200 text-sm" data-id="element-1322">
                Fat disabled people—especially at larger sizes—face compounded
                barriers. Mobility aids have weight limits. Medical providers
                attribute all symptoms to weight. Accessibility features rarely
                account for size.
              </p>
            </div>

            <div className="bg-white/10 p-6 backdrop-blur-sm border border-white/20" data-id="element-1323">
              <h3 className="font-bold text-lg text-brand-magenta mb-2" data-id="element-1324">
                Size + Gender
              </h3>
              <p className="text-gray-200 text-sm" data-id="element-1325">
                Fat women and femmes face gendered fatphobia—their worth is tied
                to desirability. Fat trans people face barriers to
                gender-affirming care that increase with size. Fat men
                experience fatphobia differently but are not exempt.
              </p>
            </div>

            <div className="bg-white/10 p-6 backdrop-blur-sm border border-white/20" data-id="element-1326">
              <h3 className="font-bold text-lg text-brand-magenta mb-2" data-id="element-1327">
                Size + Class
              </h3>
              <p className="text-gray-200 text-sm" data-id="element-1328">
                Poor fat people can't buy their way out of inaccessibility.
                Plus-size clothing is expensive. Bariatric equipment costs more.
                The "solutions" to fatphobia are often only available to those
                with money.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Important Note */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 max-w-4xl mx-auto" data-id="element-1329">
        <div className="bg-white border-2 border-brand-black p-8" data-id="element-1330">
          <div className="flex items-start gap-4" data-id="element-1331">
            <AlertTriangle className="h-8 w-8 text-brand-magenta flex-shrink-0 mt-1" data-id="element-1332" />
            <div data-id="element-1333">
              <h3 className="text-xl font-black uppercase mb-4 text-brand-black" data-id="element-1334">
                A Note on Using This Framework
              </h3>
              <div className="space-y-3 text-gray-700" data-id="element-1335">
                <p data-id="element-1336">
                  These categories are{' '}
                  <strong data-id="element-1337">tools for analysis, not rigid boxes</strong>. People's
                  sizes change. Bodies are complex. The goal isn't to police who
                  belongs where—it's to name the reality that size affects
                  access and to center those with the least.
                </p>
                <p data-id="element-1338">
                  <strong data-id="element-1339">This framework should never be used to:</strong>
                </p>
                <ul className="space-y-2 ml-4" data-id="element-1340">
                  <li className="flex items-start" data-id="element-1341">
                    <span className="text-brand-magenta mr-2" data-id="element-1342">✗</span>Gate-keep
                    who is "fat enough" for fat spaces
                  </li>
                  <li className="flex items-start" data-id="element-1343">
                    <span className="text-brand-magenta mr-2" data-id="element-1344">✗</span>Shame
                    people for their size category
                  </li>
                  <li className="flex items-start" data-id="element-1345">
                    <span className="text-brand-magenta mr-2" data-id="element-1346">✗</span>Create
                    hierarchies of suffering
                  </li>
                  <li className="flex items-start" data-id="element-1347">
                    <span className="text-brand-magenta mr-2" data-id="element-1348">✗</span>Dismiss
                    smallfat experiences of fatphobia
                  </li>
                </ul>
                <p data-id="element-1349">
                  <strong data-id="element-1350">It should be used to:</strong>
                </p>
                <ul className="space-y-2 ml-4" data-id="element-1351">
                  <li className="flex items-start" data-id="element-1352">
                    <span className="text-brand-lime mr-2" data-id="element-1353">✓</span>Center the
                    most marginalized in fat spaces
                  </li>
                  <li className="flex items-start" data-id="element-1354">
                    <span className="text-brand-lime mr-2" data-id="element-1355">✓</span>Acknowledge
                    privilege within fatness
                  </li>
                  <li className="flex items-start" data-id="element-1356">
                    <span className="text-brand-lime mr-2" data-id="element-1357">✓</span>Design spaces
                    and resources for the largest bodies
                  </li>
                  <li className="flex items-start" data-id="element-1358">
                    <span className="text-brand-lime mr-2" data-id="element-1359">✓</span>Understand
                    how oppression scales with size
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Further Reading */}
      <section className="py-16 bg-white" data-id="element-1360">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1361">
          <div className="flex items-center gap-3 mb-8" data-id="element-1362">
            <Lightbulb className="h-8 w-8 text-brand-magenta" data-id="element-1363" />
            <h2 className="text-3xl font-black uppercase text-brand-black" data-id="element-1364">
              Further Reading
            </h2>
          </div>
          <p className="text-gray-600 mb-8 max-w-3xl" data-id="element-1365">
            The fat spectrum is one framework among many. These related theories
            and perspectives deepen the analysis.
          </p>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-id="element-1366">
            <Link to="/clusters/categories-of-fat-access" className="group block" data-id="element-1367">
              <div className="bg-brand-magenta/5 border-2 border-brand-magenta p-6 hover:shadow-lg transition-shadow h-full flex flex-col" data-id="element-1368">
                <span className="text-xs font-bold uppercase tracking-wider text-brand-magenta mb-2" data-id="element-1369">
                  ReGrowth Framework
                </span>
                <h3 className="text-lg font-bold text-brand-black group-hover:text-brand-magenta transition-colors mb-2" data-id="element-1370">
                  Categories of Fat Access
                </h3>
                <p className="text-sm text-gray-600 flex-grow" data-id="element-1371">
                  The key domains where fat people face structural barriers —
                  from physical space to healthcare.
                </p>
                <ArrowRight className="h-4 w-4 text-brand-magenta mt-4 group-hover:translate-x-1 transition-transform" data-id="element-1372" />
              </div>
            </Link>

            <div className="border border-gray-200 p-6" data-id="element-1373">
              <span className="text-xs font-bold uppercase tracking-wider text-gray-500 mb-2 block" data-id="element-1374">
                Theory
              </span>
              <h3 className="text-lg font-bold text-brand-black mb-2" data-id="element-1375">
                Anti-Fatness as Anti-Blackness
              </h3>
              <p className="text-xs text-gray-500 mb-2" data-id="element-1376">
                Sabrina Strings / Da'Shaun L. Harrison
              </p>
              <p className="text-sm text-gray-600" data-id="element-1377">
                How modern fatphobia is rooted in anti-Black racism and colonial
                beauty standards.
              </p>
            </div>

            <div className="border border-gray-200 p-6" data-id="element-1378">
              <span className="text-xs font-bold uppercase tracking-wider text-gray-500 mb-2 block" data-id="element-1379">
                Framework
              </span>
              <h3 className="text-lg font-bold text-brand-black mb-2" data-id="element-1380">
                Health at Every Size (HAES)
              </h3>
              <p className="text-xs text-gray-500 mb-2" data-id="element-1381">Lindo Bacon / ASDAH</p>
              <p className="text-sm text-gray-600" data-id="element-1382">
                A weight-neutral approach to health that challenges the
                medicalisation of fatness.
              </p>
            </div>

            <div className="border border-gray-200 p-6" data-id="element-1383">
              <span className="text-xs font-bold uppercase tracking-wider text-gray-500 mb-2 block" data-id="element-1384">
                Social Movement
              </span>
              <h3 className="text-lg font-bold text-brand-black mb-2" data-id="element-1385">
                Fat Activism
              </h3>
              <p className="text-xs text-gray-500 mb-2" data-id="element-1386">Charlotte Cooper</p>
              <p className="text-sm text-gray-600" data-id="element-1387">
                An analysis of fat activism as a diverse, creative, and
                politically radical social movement.
              </p>
            </div>

            <div className="border border-gray-200 p-6" data-id="element-1388">
              <span className="text-xs font-bold uppercase tracking-wider text-gray-500 mb-2 block" data-id="element-1389">
                Intersection
              </span>
              <h3 className="text-lg font-bold text-brand-black mb-2" data-id="element-1390">
                Disability Justice & Fatness
              </h3>
              <p className="text-xs text-gray-500 mb-2" data-id="element-1391">
                Sins Invalid / Piepzna-Samarasinha
              </p>
              <p className="text-sm text-gray-600" data-id="element-1392">
                Shared struggles around access, medicalisation, and bodily
                autonomy.
              </p>
            </div>

            <Link to="/clusters/fat-theory-catalogue" className="group block" data-id="element-1393">
              <div className="bg-brand-navy p-6 hover:shadow-lg transition-shadow h-full flex flex-col justify-center text-center" data-id="element-1394">
                <BookOpen className="h-8 w-8 text-white mx-auto mb-3" data-id="element-1395" />
                <h3 className="text-lg font-bold text-white group-hover:text-brand-magenta transition-colors mb-2" data-id="element-1396">
                  Browse All Theories
                </h3>
                <p className="text-sm text-gray-300" data-id="element-1397">
                  View the full Fat Theory Catalogue
                </p>
                <ArrowRight className="h-4 w-4 text-white mx-auto mt-4 group-hover:translate-x-1 transition-transform" data-id="element-1398" />
              </div>
            </Link>
          </div>
        </div>
      </section>

      {/* Critique */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 max-w-4xl mx-auto" data-id="element-1399">
        <h2 className="text-3xl font-black uppercase mb-8 text-brand-black" data-id="element-1400">
          Critique & Limitations
        </h2>
        <div className="space-y-6 text-gray-700 leading-relaxed" data-id="element-1401">
          <p data-id="element-1402">
            The fat spectrum framework is a useful analytical tool, but it is
            not without limitations. Engaging critically with any framework
            strengthens our understanding.
          </p>
          <div className="space-y-4" data-id="element-1403">
            <div className="border-l-4 border-brand-magenta pl-6" data-id="element-1404">
              <h3 className="font-bold text-brand-black mb-2" data-id="element-1405">
                Rigidity of categories
              </h3>
              <p className="text-sm" data-id="element-1406">
                Size categories can feel rigid when bodies are fluid. Weight
                fluctuates, and people may move between categories over time.
                The framework risks creating fixed identities from what is
                actually a spectrum.
              </p>
            </div>
            <div className="border-l-4 border-brand-magenta pl-6" data-id="element-1407">
              <h3 className="font-bold text-brand-black mb-2" data-id="element-1408">
                US-centric sizing
              </h3>
              <p className="text-sm" data-id="element-1409">
                The size ranges (US 14–18, etc.) are based on American sizing
                standards and may not translate directly to other countries or
                cultural contexts where body size carries different social
                meanings.
              </p>
            </div>
            <div className="border-l-4 border-brand-magenta pl-6" data-id="element-1410">
              <h3 className="font-bold text-brand-black mb-2" data-id="element-1411">
                Risk of hierarchy
              </h3>
              <p className="text-sm" data-id="element-1412">
                While the framework aims to centre the most marginalised, it can
                inadvertently create a "hierarchy of suffering" where smaller
                fat people feel their experiences are dismissed. The goal is
                solidarity, not competition.
              </p>
            </div>
            <div className="border-l-4 border-brand-magenta pl-6" data-id="element-1413">
              <h3 className="font-bold text-brand-black mb-2" data-id="element-1414">
                Intersectional complexity
              </h3>
              <p className="text-sm" data-id="element-1415">
                Size alone doesn't determine experience. A white infinifat
                person and a Black smallfat person may face comparable levels of
                systemic oppression through different mechanisms. The framework
                must always be read alongside race, disability, gender, and
                class.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* References */}
      <section className="py-16 bg-white" data-id="element-1416">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1417">
          <h2 className="text-2xl font-black uppercase mb-8 text-brand-black" data-id="element-1418">
            References
          </h2>
          <div className="space-y-4" data-id="element-1419">
            {[{
            title: 'Fearing the Black Body: The Racial Origins of Fat Phobia',
            author: 'Sabrina Strings (2019)'
          }, {
            title: 'Belly of the Beast: The Politics of Anti-Fatness as Anti-Blackness',
            author: "Da'Shaun L. Harrison (2021)"
          }, {
            title: "What We Don't Talk About When We Talk About Fat",
            author: 'Aubrey Gordon (2020)'
          }, {
            title: 'The Body Is Not an Apology',
            author: 'Sonya Renee Taylor (2018)'
          }, {
            title: 'Fat Activism: A Radical Social Movement',
            author: 'Charlotte Cooper (2016)'
          }, {
            title: 'Unshrinking: How to Face Fatphobia',
            author: 'Kate Manne (2024)'
          }, {
            title: 'Health at Every Size',
            author: 'Lindo Bacon (2010)'
          }, {
            title: 'Fat Shame: Stigma and the Fat Body in American Culture',
            author: 'Amy Erdman Farrell (2011)'
          }].map((ref, index) => <div key={index} className="flex items-start gap-3 py-3 border-b border-gray-100 last:border-b-0" data-id="element-1420">
                <BookOpen className="h-4 w-4 text-brand-magenta flex-shrink-0 mt-1" data-id="element-1421" />
                <div data-id="element-1422">
                  <p className="font-bold text-brand-black text-sm" data-id="element-1423">
                    {ref.title}
                  </p>
                  <p className="text-xs text-gray-500" data-id="element-1424">{ref.author}</p>
                </div>
              </div>)}
          </div>
        </div>
      </section>
    </div>;
}